import { useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "sonner";
import { EventDataTypeEnum } from "../../../../../store/types/work.types";
import type { EventData } from "../../../../../store/types/work.types";
import { useDownloadEventDataFileMutation } from "../../../../../store/services/timelineApiRTK";
import {
  useUploadSecureVolumeFileMutation,
  useDeleteEventDataMutation,
} from "../../../../../store/services/secureVolumeService";
import { UPLOAD_CONFIGS, type UploadVariant } from "./uploadConfigs";

interface ContractFilesButtonProps {
  eventDatas: EventData[];
  customerId: number;
  workId: number;
  hasContract: boolean;
  hasSigned: boolean;
}

interface ContractFile {
  id: number;
  type: EventDataTypeEnum;
  label: string;
  filename: string;
}

const parseFilename = (data: string): string => {
  try {
    const parsed = JSON.parse(data);
    return parsed?.OrginalFilename || parsed?.Filename || "Okänd fil";
  } catch {
    return "Okänd fil";
  }
};

export const ContractFilesButton = ({ eventDatas, customerId, workId, hasContract, hasSigned }: ContractFilesButtonProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadVariant, setUploadVariant] = useState<UploadVariant>("contract");
  const [deleteTarget, setDeleteTarget] = useState<ContractFile | null>(null);
  const [downloadFile, { isLoading: isDownloading }] = useDownloadEventDataFileMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadSecureVolumeFileMutation();
  const [deleteEventData, { isLoading: isDeleting }] = useDeleteEventDataMutation();

  const isLoading = isDownloading || isUploading || isDeleting;

  const contractFiles: ContractFile[] = eventDatas
    .filter(
      (e) =>
        e.Type === EventDataTypeEnum.SecureVolumeContractFile ||
        e.Type === EventDataTypeEnum.SecureVolumeSignedContractFile,
    )
    .map((e) => ({
      id: e.Id,
      type: e.Type,
      label:
        e.Type === EventDataTypeEnum.SecureVolumeSignedContractFile
          ? "Signerat avtal"
          : "Avtal",
      filename: parseFilename(e.Data),
    }));


  const handleDownload = async (file: ContractFile) => {
    setOpen(false);
    try {
      await downloadFile({ eventDataId: file.id, filename: file.filename }).unwrap();
    } catch {
      toast.error("Det gick inte att ladda ner filen.");
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, file: ContractFile) => {
    e.stopPropagation();
    setOpen(false);
    setDeleteTarget(file);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteTarget(null);
    try {
      await deleteEventData({ eventDataId: deleteTarget.id, workId }).unwrap();
      toast.success("Fil borttagen!");
    } catch {
      toast.error("Det gick inte att ta bort filen.");
    }
  };

  const triggerUpload = (variant: UploadVariant) => {
    setUploadVariant(variant);
    setOpen(false);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const config = UPLOAD_CONFIGS[uploadVariant];
    try {
      await uploadFile({
        customerId,
        workId,
        file,
        eventDataType: config.eventDataType,
      }).unwrap();
      toast.success("Fil uppladdad!");
    } catch {
      toast.error("Det gick inte att ladda upp filen.");
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".pdf,.doc,.docx"
        onChange={handleFileSelected}
      />
      <Tooltip title="Avtalsfiler">
        <IconButton
          ref={anchorRef}
          size="small"
          onClick={() => setOpen(true)}
          color={hasSigned ? "success" : "default"}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Badge badgeContent={contractFiles.length} color="primary" max={9}>
              <FolderIcon />
            </Badge>
          )}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorRef.current} open={open} onClose={() => setOpen(false)}>
        {contractFiles.map((file) => (
          <MenuItem key={file.id} onClick={() => handleDownload(file)}>
            <ListItemIcon>
              {file.type === EventDataTypeEnum.SecureVolumeSignedContractFile ? (
                <VerifiedUserIcon fontSize="small" color="success" />
              ) : (
                <DescriptionIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={file.label}
              secondary={
                <Typography variant="caption" noWrap sx={{ maxWidth: 200, display: "block" }}>
                  {file.filename}
                </Typography>
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", ml: 1, gap: 0.5 }}>
              <DownloadIcon fontSize="small" color="action" />
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(e, file)}
                sx={{ p: 0.5 }}
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>

            </Box>
          </MenuItem>
        ))}
        {(!hasContract || !hasSigned) && contractFiles.length > 0 && <Divider />}
        {!hasContract && (
          <MenuItem onClick={() => triggerUpload("contract")}>
            <ListItemIcon>
              <UploadFileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ladda upp avtal</ListItemText>
          </MenuItem>
        )}
        {!hasSigned && (
          <MenuItem onClick={() => triggerUpload("signed")}>
            <ListItemIcon>
              <UploadFileIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Ladda upp signerat avtal</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Ta bort fil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Är du säker på att du vill ta bort <strong>{deleteTarget?.filename}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Avbryt</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
